import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, DataTable, Select, StatTile, StatusBadge, useLocale, useToast, type DataTableColumn } from '@ministry/ui/internal';

import { getAppointmentPolicy, listAppointments, type AppointmentRow } from '../lib/appointments.js';
import { appointmentStatusEntry, availabilityStatusEntry } from '../lib/appointment-lifecycle.js';
import { ApiError } from '../lib/api-client.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { formatInstantDisplay } from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';
import { ForbiddenState } from '../components/ForbiddenState.js';
import {
  AppointmentCancelModal,
  AppointmentDeclineModal,
  AppointmentScheduleModal,
} from '../components/AppointmentActionModals.js';

const PAGE_SIZE = 25;

/**
 * SCREEN_SPECS.md I14. §13.6's nine endpoints, all live — this is the screen that had
 * NONE of them wired ("an officer cannot see, accept, reschedule or decline a single visit
 * request" per this build's brief). Newest/soonest-first (server's own default order,
 * confirmed live), with a Pending count that also drives the nav badge (`AppShell`
 * reads the same query key).
 */
export function AppointmentInboxPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = React.useState<string>('pending');
  type ActiveModal = { kind: 'accept' | 'reschedule' | 'decline' | 'cancel'; row: AppointmentRow } | null;
  const [activeModal, setActiveModal] = React.useState<ActiveModal>(null);

  const policyQuery = useQuery({
    queryKey: ['appointment-policy'],
    queryFn: ({ signal }) => getAppointmentPolicy(signal),
    staleTime: 5 * 60_000,
  });

  const query = useQuery({
    queryKey: ['appointments', 'inbox', statusFilter],
    queryFn: ({ signal }) => listAppointments({ statusCode: statusFilter || undefined, pageSize: PAGE_SIZE }, signal),
  });

  // The Pending count needs its OWN query, independent of whatever tab is active, so the
  // badge does not disappear just because the officer is looking at a different tab.
  const pendingCountQuery = useQuery({
    queryKey: ['appointments', 'inbox', 'pending-count'],
    queryFn: ({ signal }) => listAppointments({ statusCode: 'pending', pageSize: 1 }, signal),
  });

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['appointments'] });
  };
  const notifySuccess = (key: string) => push({ variant: 'success', title: t(key) });

  if (query.isError && query.error instanceof ApiError && query.error.code.startsWith('FORBIDDEN')) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('appointments.inbox.title')} />
        <ForbiddenState error={query.error} />
      </div>
    );
  }

  const rows = query.data?.rows ?? [];
  const reasons = policyQuery.data?.declineReasons ?? [];

  const columns: DataTableColumn<AppointmentRow>[] = [
    {
      key: 'when',
      header: t('appointments.column.when'),
      visibleFrom: 'always',
      cardSlot: 'title',
      minWidth: '12rem',
      render: (row) => formatInstantDisplay(row.scheduledStart ?? row.requestedStart, locale),
    },
    {
      key: 'status',
      header: t('appointments.column.status'),
      visibleFrom: 'always',
      cardSlot: 'status',
      render: (row) => <StatusBadge entry={appointmentStatusEntry(row.status)} />,
    },
    {
      key: 'visitor',
      header: t('appointments.column.visitor'),
      visibleFrom: 'always',
      cardSlot: 'line2',
      grow: true,
      render: (row) => (
        <span>
          {row.visitor.name}
          <span className="block text-xs text-text-muted">{row.visitor.phone}</span>
        </span>
      ),
    },
    {
      key: 'purpose',
      header: t('appointments.column.purpose'),
      visibleFrom: 'md',
      cardSlot: 'line3',
      render: (row) => <span className="line-clamp-2">{row.purpose}</span>,
    },
    {
      key: 'availability',
      header: t('appointments.column.myAvailability'),
      visibleFrom: 'lg',
      cardSlot: 'line4',
      render: (row) =>
        row.targetAvailability ? (
          <StatusBadge
            entry={availabilityStatusEntry(row.targetAvailability.statusCode, row.targetAvailability.labelNe, row.targetAvailability.labelEn)}
            variant="tint"
          />
        ) : (
          <span className="text-text-muted">{t('common.notAvailable')}</span>
        ),
    },
    {
      // No `cardSlot`: `DataTable`'s narrow (<768px) card layout has no "actions" slot at
      // all (`title`/`status`/`line2-4`/two badge slots only), so these buttons are
      // reachable at md+ width; below it, the row itself is still a full link to the
      // Appointment Detail page (I15/route below), which renders the SAME action set for
      // one appointment — a tap-through, not a missing control.
      key: 'actions',
      header: t('appointments.column.actions'),
      visibleFrom: 'md',
      render: (row) => {
        const canDecideFurther = row.status.code === 'pending' || row.status.code === 'accepted' || row.status.code === 'rescheduled';
        if (!canDecideFurther) return <span className="text-text-muted">—</span>;
        return (
          // `DataTable`'s own established pattern (its bulk-select checkbox cell, same
          // file) for exactly this: a click inside a cell that ALSO has `onRowClick`
          // bubbles up to the row's own `onClick` and navigates away before the intended
          // action (opening a modal) ever takes effect — confirmed live, driving this
          // screen against the running server: clicking "Accept" silently navigated to
          // the detail page instead of opening the Accept modal. Stopped at the cell.
          <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
            {row.status.code === 'pending' ? (
              <Button size="sm" onClick={() => setActiveModal({ kind: 'accept', row })}>
                {t('appointments.action.accept')}
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setActiveModal({ kind: 'reschedule', row })}>
                {t('appointments.action.reschedule')}
              </Button>
            )}
            {row.status.code === 'pending' ? (
              <Button size="sm" variant="destructive" onClick={() => setActiveModal({ kind: 'decline', row })}>
                {t('appointments.action.decline')}
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setActiveModal({ kind: 'cancel', row })}>
                {t('appointments.action.cancel')}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const statusOptions = [
    { value: 'pending', label: t('appointments.tab.pending') },
    { value: 'accepted', label: t('appointments.tab.accepted') },
    { value: 'rescheduled', label: t('appointments.tab.rescheduled') },
    { value: 'declined', label: t('appointments.tab.declined') },
    { value: 'completed', label: t('appointments.tab.completed') },
    { value: 'cancelled', label: t('appointments.tab.cancelled') },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('appointments.inbox.title')} description={t('appointments.inbox.description')} />

      <div className="flex flex-wrap gap-3">
        <StatTile
          className="min-w-[220px] flex-1"
          label={t('appointments.statPending')}
          value={pendingCountQuery.data?.page.totalItems}
          loading={pendingCountQuery.isLoading}
          error={pendingCountQuery.isError}
          onRetry={() => pendingCountQuery.refetch()}
          accent={(pendingCountQuery.data?.page.totalItems ?? 0) > 0 ? 'serious' : 'default'}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3 border-b border-border-decorative pb-4">
        <div className="w-48">
          <Select
            id="appointment-inbox-status"
            label={t('appointments.tab.label')}
            placeholder={t('appointments.tab.all')}
            value={statusFilter || undefined}
            onValueChange={(value) => setStatusFilter(value)}
            options={statusOptions}
          />
        </div>
        {statusFilter ? (
          <Button variant="ghost" size="sm" onClick={() => setStatusFilter('')}>
            {t('common.clearFilters')}
          </Button>
        ) : null}
      </div>

      <DataTable
        caption={t('appointments.inbox.caption')}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => query.refetch()}
        onRowClick={(row) => navigate(ROUTES.appointmentDetail(row.id))}
        emptyTitle={statusFilter === 'pending' ? t('appointments.empty.pending.title') : t('appointments.empty.filtered.title')}
        emptyBody={statusFilter === 'pending' ? t('appointments.empty.pending.body') : t('appointments.empty.filtered.body')}
      />

      {activeModal?.kind === 'accept' || activeModal?.kind === 'reschedule' ? (
        <AppointmentScheduleModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          appointment={activeModal.row}
          mode={activeModal.kind}
          onDone={() => {
            invalidateAll();
            notifySuccess(activeModal.kind === 'accept' ? 'toast.appointments.accepted' : 'toast.appointments.rescheduled');
          }}
          onStaleConflict={invalidateAll}
        />
      ) : null}
      {activeModal?.kind === 'decline' ? (
        <AppointmentDeclineModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          appointment={activeModal.row}
          reasons={reasons}
          onDone={() => {
            invalidateAll();
            notifySuccess('toast.appointments.declined');
          }}
          onStaleConflict={invalidateAll}
        />
      ) : null}
      {activeModal?.kind === 'cancel' ? (
        <AppointmentCancelModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          appointment={activeModal.row}
          onDone={() => {
            invalidateAll();
            notifySuccess('toast.appointments.cancelled');
          }}
          onStaleConflict={invalidateAll}
        />
      ) : null}
    </div>
  );
}
